import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, MapPin, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { BRAND } from "@/data/site";
import { submitLead } from "@/lib/api";
import { trackLeadConversion } from "@/lib/analytics";
import { useSeo } from "@/lib/seo";

const REQUIREMENTS = [
  "Business Database", "Newly Registered Company", "GST Database", "GST Sales & Purchase",
  "B2B & B2C Database", "E-Commerce Sellers & Buyers", "Doctors Database",
  "Restaurant Database", "Real Estate Database", "Import Export Database",
  "Vehicle Owner Database", "Stock Market Database", "Insurance Database",
  "Loan Database", "Custom / Other",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", requirement: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Contact Datorra India — Call +91 85379 42196 for Business Database Quote",
    description:
      "Get a customized quote for business, B2B, GST, real estate, stock market or telecalling databases across India. Call or WhatsApp +91 85379 42196. Office: Trade Centre, BKC, Mumbai.",
    path: "/contact",
  });

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ ...form, source: "contact-page" });
      trackLeadConversion();
      setDone(true);
      toast.success("Thanks — we'll be in touch within business hours.");
      setForm({ name: "", company: "", email: "", phone: "", requirement: "", message: "" });
    } catch (err) {
      toast.error("We couldn't send that. Please try WhatsApp or email.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="Contact data providers"
        italicWord="you can trust."
        description="Looking for calling data providers near you? Datorra is a Pan India database provider based in Mumbai — call or WhatsApp us for real estate calling data, stock market calling data, loan data, mobile number database, B2B contact database provider services and more. Most enquiries hear back within 30 minutes on business hours."
      />

      <section className="py-16 md:py-24 bg-[#fafafa] border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7 bg-white rounded-3xl border border-slate-100 p-8 md:p-12"
          >
            <div className="text-xs uppercase tracking-[0.24em] text-blue-600 font-semibold">/ Enquiry Form</div>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-light tracking-tight text-slate-900">
              Start a <span className="font-serif-editorial italic">conversation</span>.
            </h2>

            {done ? (
              <div className="mt-12 flex flex-col items-start" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <Check className="w-6 h-6 text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="mt-6 font-display text-2xl text-slate-900">We've got it.</div>
                <div className="mt-2 text-slate-600">A member of our team will reach out shortly.</div>
                <button data-testid="contact-send-another" onClick={() => setDone(false)} className="mt-6 text-sm text-blue-600 underline underline-offset-4">Send another enquiry</button>
              </div>
            ) : (
              <form onSubmit={handle} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contact-form">
                <Field id="contact-name" label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <Field id="contact-company" label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
                <Field id="contact-email" label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <Field id="contact-phone" label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Requirement</label>
                  <select
                    data-testid="contact-requirement"
                    value={form.requirement}
                    onChange={(e) => setForm({ ...form, requirement: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-3 text-slate-900 text-base outline-none"
                  >
                    <option value="">Select a category</option>
                    {REQUIREMENTS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">Message</label>
                  <textarea
                    data-testid="contact-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="ICP, geography, use case — anything that helps us scope."
                    className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-3 text-slate-900 text-base outline-none resize-none"
                  />
                </div>
                <div className="md:col-span-2 mt-2">
                  <button
                    data-testid="contact-submit"
                    disabled={loading}
                    className="group inline-flex items-center gap-2 rounded-full bg-slate-900 text-white pl-7 pr-6 py-4 text-sm font-medium hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-500/20 transition-all disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Submit Enquiry"}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.75} />
                  </button>
                  <div className="mt-4 text-xs text-slate-400">
                    By submitting, you agree to be contacted about your enquiry. We never share your data.
                  </div>
                </div>
              </form>
            )}
          </motion.div>

          <div className="md:col-span-5 space-y-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="bg-slate-900 text-white rounded-3xl p-8 md:p-10"
            >
              <div className="text-xs uppercase tracking-[0.24em] text-blue-300 font-semibold">/ Direct</div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl font-light tracking-tight">
                Reach us <span className="font-serif-editorial italic">directly</span>.
              </h3>
              <div className="mt-8 space-y-5">
                <a href={`mailto:${BRAND.email}`} data-testid="contact-info-email" className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-blue-300">Email</div>
                    <div className="text-white group-hover:text-blue-200 transition-colors">{BRAND.email}</div>
                  </div>
                </a>
                <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} data-testid="contact-info-phone" className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-blue-300">Phone</div>
                    <div className="text-white">{BRAND.phone}</div>
                  </div>
                </a>
                <a href={`https://wa.me/${BRAND.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" data-testid="contact-info-whatsapp" className="flex items-center gap-4 group">
                  <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                    <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-blue-300">WhatsApp</div>
                    <div className="text-white">{BRAND.whatsapp} — reply under 30 min</div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-blue-300">Office</div>
                    <div className="text-white/90">{BRAND.address}</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="rounded-3xl overflow-hidden border border-slate-100 aspect-[4/3] bg-slate-100"
              data-testid="contact-map"
            >
              <iframe
                title="Datorra India Office Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.0289499354147!2d72.86822!3d19.06697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c8371fcd6fe1%3A0x0!2sBandra%20Kurla%20Complex!5e0!3m2!1sen!2sin!4v1700000000000"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">{label}{required && "*"}</label>
      <input
        data-testid={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border-b border-slate-200 focus:border-slate-900 py-3 text-slate-900 text-base outline-none transition-colors"
      />
    </div>
  );
}
